// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useMergeRefs } from '@cloudscape-design/component-toolkit/internal';
import InternalIcon from '../../../../icon/internal.js';
import Tooltip from '../../../../tooltip/internal.js';
import { getBaseProps } from '../../../base-component';
import useHiddenDescription from '../../../hooks/use-hidden-description';
import CheckboxIcon from '../../checkbox-icon';
import Option from '../../option';
import SelectableItem from '../../selectable-item';
import styles from '../../../../select/parts/styles.css.js';
const toSelectOptionGroupItem = (props) => {
    var _a;
    return {
        type: 'group',
        index: (_a = props.virtualIndex) !== null && _a !== void 0 ? _a : props.index,
        option: props.option.option,
        disabled: props.disabled,
    };
};
const toSelectOptionItem = (props) => {
    var _a, _b, _c, _d;
    return {
        type: 'item',
        index: (_a = props.virtualIndex) !== null && _a !== void 0 ? _a : props.index,
        option: props.option.option,
        selected: props.selected,
        highlighted: props.highlighted,
        disabled: props.disabled,
        parent: props.parentProps
            ? toSelectOptionGroupItem({
                index: (_b = props.parentProps) === null || _b === void 0 ? void 0 : _b.index,
                virtualIndex: (_c = props.parentProps) === null || _c === void 0 ? void 0 : _c.virtualIndex,
                option: (_d = props.parentProps) === null || _d === void 0 ? void 0 : _d.option,
                disabled: props.disabled,
            })
            : null,
    };
};
const toMultiselectOptionGroupItem = (props) => {
    var _a, _b;
    return {
        type: 'group',
        index: (_a = props.virtualIndex) !== null && _a !== void 0 ? _a : props.index,
        option: props.option.option,
        indeterminate: (_b = props.indeterminate) !== null && _b !== void 0 ? _b : false,
        selected: props.selected,
        highlighted: props.highlighted,
        disabled: props.disabled,
    };
};
const toMultiselectOptionItem = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    return {
        type: 'item',
        index: (_a = props.virtualIndex) !== null && _a !== void 0 ? _a : props.index,
        option: props.option.option,
        selected: props.selected,
        highlighted: props.highlighted,
        disabled: props.disabled,
        parent: props.parentProps
            ? toMultiselectOptionGroupItem({
                index: (_b = props.parentProps) === null || _b === void 0 ? void 0 : _b.index,
                virtualIndex: (_c = props.parentProps) === null || _c === void 0 ? void 0 : _c.virtualIndex,
                option: (_d = props.parentProps) === null || _d === void 0 ? void 0 : _d.option,
                disabled: props.disabled,
                highlighted: (_f = (_e = props.parentProps) === null || _e === void 0 ? void 0 : _e.highlighted) !== null && _f !== void 0 ? _f : false,
                indeterminate: (_h = (_g = props.parentProps) === null || _g === void 0 ? void 0 : _g.indeterminate) !== null && _h !== void 0 ? _h : false,
                selected: (_k = (_j = props.parentProps) === null || _j === void 0 ? void 0 : _j.selected) !== null && _k !== void 0 ? _k : false,
            })
            : null,
    };
};
const Item = ({ index, virtualIndex, option, selectionType = 'single', highlighted, selected, indeterminate, filteringValue, virtualPosition, padBottom, isNextSelected, isPreviousSelected, screenReaderContent, ariaPosinset, ariaSetsize, highlightType, withScrollbar, sticky, renderOption, parentProps, ...restProps }, ref) => {
    const baseProps = getBaseProps(restProps);
    const isMulti = selectionType === 'multi';
    const isParent = option.type === 'parent';
    const isChild = option.type === 'child';
    const isSelectAll = option.type === 'select-all';
    const wrappedOption = option.option;
    const disabled = option.disabled || wrappedOption.disabled;
    const disabledReasonSource = isMulti
        ? option.disabledReason || wrappedOption.disabledReason
        : wrappedOption.disabledReason;
    const disabledReason = disabled && disabledReasonSource ? disabledReasonSource : '';
    const isDisabledWithReason = !!disabledReason;
    const internalRef = useRef(null);
    const { descriptionEl, descriptionId } = useHiddenDescription(disabledReason);
    const [canShowTooltip, setCanShowTooltip] = useState(true);
    useEffect(() => setCanShowTooltip(true), [highlighted]);
    const mergedRef = useMergeRefs(ref, internalRef);
    const getSelectItemProps = (option) => {
        if (option.type === 'parent') {
            return toSelectOptionGroupItem({
                option: option,
                index: index,
                virtualIndex: virtualIndex,
                disabled: !!disabled,
            });
        }
        else {
            return toSelectOptionItem({
                option: option,
                index: index,
                virtualIndex: virtualIndex,
                disabled: !!disabled,
                highlighted: !!highlighted,
                selected: !!selected,
                parentProps: parentProps,
            });
        }
    };
    const getMultiselectItemProps = (option) => {
        if (option.type === 'parent') {
            return toMultiselectOptionGroupItem({
                option: option,
                index: index,
                virtualIndex: virtualIndex,
                disabled: !!disabled,
                highlighted: !!highlighted,
                selected: !!selected,
                indeterminate: indeterminate !== null && indeterminate !== void 0 ? indeterminate : false,
            });
        }
        else if (option.type === 'select-all') {
            return {
                type: 'select-all',
                option: option.option,
                indeterminate: indeterminate !== null && indeterminate !== void 0 ? indeterminate : false,
                selected: !!selected,
                highlighted: !!highlighted,
            };
        }
        else {
            return toMultiselectOptionItem({
                option: option,
                index: index,
                virtualIndex: virtualIndex,
                disabled: !!disabled,
                highlighted: !!highlighted,
                selected: !!selected,
                parentProps: parentProps,
            });
        }
    };
    const renderOptionWrapper = (option) => {
        if (!renderOption) {
            return null;
        }
        if (isMulti) {
            return renderOption({
                item: getMultiselectItemProps(option),
                filterText: filteringValue,
            });
        }
        return renderOption({
            item: getSelectItemProps(option),
            filterText: filteringValue,
        });
    };
    const renderResult = renderOptionWrapper(option);
    const optionElement = (React.createElement(Option, { customContent: renderResult, option: { ...wrappedOption, disabled }, highlightedOption: highlighted, selectedOption: selected, highlightText: filteringValue, isGroupOption: isParent }));
    const disabledReasonElement = !renderResult && isDisabledWithReason && (React.createElement(React.Fragment, null,
        descriptionEl,
        highlighted && canShowTooltip && (React.createElement(Tooltip, { className: styles['disabled-reason-tooltip'], getTrack: () => internalRef.current, content: disabledReason, position: "right", onEscape: () => setCanShowTooltip(false) }))));
    if (isMulti) {
        return (React.createElement(SelectableItem, { disableContentStyling: !!renderResult, ariaSelected: isParent && indeterminate ? undefined : Boolean(selected), ariaChecked: isParent && indeterminate ? 'mixed' : Boolean(selected), selected: selected, isNextSelected: isNextSelected, isPreviousSelected: isPreviousSelected, highlighted: highlighted, disabled: disabled, isParent: isParent, isChild: isChild, isSelectAll: isSelectAll, highlightType: highlightType, ref: mergedRef, virtualPosition: virtualPosition, padBottom: padBottom, useInteractiveGroups: true, screenReaderContent: screenReaderContent, ariaPosinset: ariaPosinset, ariaSetsize: ariaSetsize, ariaDescribedby: isDisabledWithReason ? descriptionId : '', value: option.option.value, afterHeader: option.afterHeader, withScrollbar: withScrollbar, sticky: sticky, ...baseProps },
            React.createElement("div", { className: styles.item },
                !renderResult && (React.createElement("div", { className: styles.checkbox },
                    React.createElement(CheckboxIcon, { checked: selected, indeterminate: (isParent || isSelectAll) && indeterminate, disabled: disabled }))),
                optionElement),
            disabledReasonElement));
    }
    return (React.createElement(SelectableItem, { disableContentStyling: !!renderResult, ariaSelected: Boolean(selected), selected: selected, isNextSelected: isNextSelected, isPreviousSelected: isPreviousSelected, highlighted: highlighted, disabled: option.disabled, isParent: isParent, isChild: isChild, ref: mergedRef, virtualPosition: virtualPosition, padBottom: padBottom, screenReaderContent: screenReaderContent, ariaPosinset: ariaPosinset, ariaSetsize: ariaSetsize, highlightType: highlightType, ariaDescribedby: isDisabledWithReason ? descriptionId : '', value: option.option.value, withScrollbar: withScrollbar, sticky: sticky, ...baseProps },
        React.createElement("div", { className: clsx(styles.item, !isParent && wrappedOption.labelTag && styles['show-label-tag']) },
            optionElement,
            !renderResult && !isParent && selected && (React.createElement("div", { className: styles['selected-icon'] },
                React.createElement(InternalIcon, { name: "check" }))),
            disabledReasonElement)));
};
export default React.memo(React.forwardRef(Item));
//# sourceMappingURL=item.js.map